import { addMilliseconds, max } from "date-fns";

import { defined } from "@formant/core/common/defined";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IAggregateEvent } from "@formant/core/model/IAggregateEvent";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IEventHistogram } from "@formant/core/model/IEventHistogram";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IEventQuery } from "@formant/core/model/IEventQuery";
import { IIntervalEventFilter } from "@formant/core/model/IIntervalEventFilter";
import { addInterval, Interval } from "@formant/core/model/Interval";

// Uses the histogram API to generate aggregate event records.
export function queryAggregateEvents(
    start: Date,
    end: Date,
    eventFilter: IEventFilter,
    interval: Interval,
    getHistogram: (query: IIntervalEventFilter) => IEventHistogram | undefined,
): IAggregateEvent[] | undefined {
    const diff = end.getTime() - start.getTime();
    if (diff < 0) {
        throw new Error("Invalid date order");
    }
    let aggregates: IAggregateEvent[] | undefined;
    const histogram = getHistogram({
        interval,
        ...eventFilter,
        start: start.toISOString(),
        end: end.toISOString(),
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    });
    if (histogram) {
        aggregates = histogram.counts.flatMap(count => {
            const histogramIntervalStart = parseIsoDate(count.time);
            const histogramIntervalEnd = addInterval(
                histogramIntervalStart,
                interval,
            );
            const total = count.counts.total;
            if (total > 0) {
                return [
                    {
                        start: histogramIntervalStart,
                        end: histogramIntervalEnd,
                        count: total,
                        filter: eventFilter,
                    },
                ];
            } else {
                return [];
            }
        });
    }
    return aggregates;
}

// Uses the events query API to load non-aggregate events.
// Transforms the input aggregates such that records with few enough
// events are replaced with actual event records.
export function materializeEvents(
    eventFilter: IEventFilter,
    aggregates: IAggregateEvent[],
    millisecondsPerEvent: number,
    query: (query: IEventQuery) => IEvent[] | undefined,
): IEventOrAggregate[] | undefined {
    let loaded = true; // Do not return partial results
    const result = aggregates.flatMap<IEventOrAggregate>(
        (aggregate: IAggregateEvent) => {
            // The max events is the number of events that can
            // fit in the aggregate time span given the number of
            // milliseconds required for 1 event.
            const maxEvents = Math.min(
                100, // Prevent requesting too many events; "should" not be necessary
                (aggregate.end.getTime() - aggregate.start.getTime()) /
                    millisecondsPerEvent,
            );
            if (aggregate.count < maxEvents) {
                const events = query({
                    ...eventFilter,
                    start: aggregate.start.toISOString(),
                    end: addMilliseconds(aggregate.end, 1).toISOString(),
                });
                if (events) {
                    return events.map(event => ({
                        event,
                    }));
                } else {
                    loaded = false;
                    return [];
                }
            } else {
                return { aggregate };
            }
        },
    );
    return (loaded && result) || undefined;
}

// Internal function exported for test
export function reaggregateEvents(
    accumulator: IEventOrAggregate,
    value: IEventOrAggregate,
    filter: IEventFilter,
    millisecondsPerEvent: number,
): IAggregateEvent | undefined {
    let result: IAggregateEvent | undefined;

    const { event: accumulatorEvent, aggregate: accumulatorAggregate } =
        accumulator;
    const { event: valueEvent, aggregate: valueAggregate } = value;

    // Aggregate an event into an event aggregate
    if (accumulatorAggregate && valueEvent) {
        if (
            parseIsoDate(valueEvent.time).getTime() -
                accumulatorAggregate.end.getTime() <=
            millisecondsPerEvent
        ) {
            result = {
                start: accumulatorAggregate.start,
                end: max([
                    parseIsoDate(valueEvent.time),
                    accumulatorAggregate.end,
                ]),
                count: accumulatorAggregate.count + 1,
                filter,
            };
        }
    }

    // Aggregate an event aggregate into an event
    else if (accumulatorEvent && valueAggregate) {
        if (
            valueAggregate.start.getTime() -
                parseIsoDate(accumulatorEvent.time).getTime() <=
            millisecondsPerEvent
        ) {
            result = {
                start: parseIsoDate(accumulatorEvent.time),
                end: valueAggregate.end,
                count: 1 + valueAggregate.count,
                filter,
            };
        }
    }

    // Aggregate an event into an event
    else if (accumulatorEvent && valueEvent) {
        if (
            parseIsoDate(valueEvent.time).getTime() -
                parseIsoDate(accumulatorEvent.time).getTime() <=
            millisecondsPerEvent
        ) {
            result = {
                start: parseIsoDate(accumulatorEvent.time),
                end: parseIsoDate(valueEvent.time),
                count: 2,
                filter,
            };
        }
    }

    return result;
}

// Re-aggregate events and aggregates as needed for display.
export function reaggregate(
    eventsOrAggregates: IEventOrAggregate[],
    filter: IEventFilter,
    millisecondsPerEvent: number, // T2,
): IEventOrAggregate[] {
    // Sort the data as required later
    eventsOrAggregates.sort(
        (ea1: IEventOrAggregate, ea2: IEventOrAggregate) => {
            // Primary sort by event or aggregate start time
            const ea1Start =
                (ea1.event?.time && parseIsoDate(ea1.event.time)) ||
                defined(ea1.aggregate?.start);
            const ea2Start =
                (ea2.event?.time && parseIsoDate(ea2.event.time)) ||
                defined(ea2.aggregate?.start);
            if (ea1Start.getTime() < ea2Start.getTime()) {
                return -1;
            } else if (ea1Start.getTime() > ea2Start.getTime()) {
                return 1;
            }
            // Secondary sort by aggregates before events
            else if (ea1.aggregate) {
                return -1;
            } else if (ea2.aggregate) {
                return 1;
            }

            return 0;
        },
    );

    // Traverse events and aggregates to produce result such that events and
    // aggregates have been re-aggregated according to paddingMilliseconds.
    let openAggregate: IEventOrAggregate | undefined;
    const result: IEventOrAggregate[] = [];
    eventsOrAggregates.forEach(eventOrAggregate => {
        // If there is no open aggregate, the only thing to do with this
        // entity is use it to open an aggregate.
        if (!openAggregate) {
            openAggregate = eventOrAggregate;
        } else {
            // If there is an open aggregate, attempt to integrate this value
            const aggregated = reaggregateEvents(
                openAggregate,
                eventOrAggregate,
                filter,
                millisecondsPerEvent,
            );
            // Update the open aggregate if successfully integrated
            if (aggregated) {
                openAggregate = { aggregate: aggregated };
            }
            // Otherwise commit the open aggregate and begin anew
            else {
                result.push(openAggregate);
                openAggregate = eventOrAggregate;
            }
        }
    });
    if (openAggregate) {
        result.push(openAggregate);
    }
    return result;
}
